import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client.js'

export interface UseTemplatePreviewInput {
  subjectTemplate: string | null
  bodyTemplate: string
  variables: Record<string, unknown>
}

export interface UseTemplatePreviewReturn {
  subject: string | null
  body: string
  loading: boolean
  error: string | null
}

interface PreviewResponse {
  subject: string | null
  body: string
}

const DEBOUNCE_MS = 300

/**
 * Renders a notification template against sample variable values via
 * POST /admin/conversations/notification-template-preview. The request is
 * debounced (~300ms) so rapid keystrokes in the template editor don't spam
 * the endpoint. An empty bodyTemplate short-circuits to an empty preview
 * without a network call.
 */
export function useTemplatePreview(
  input: UseTemplatePreviewInput,
): UseTemplatePreviewReturn {
  const { subjectTemplate, bodyTemplate, variables } = input
  const [subject, setSubject] = useState<string | null>(null)
  const [body, setBody] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Serialize variables so the effect re-runs on value (not reference)
  // changes — callers commonly pass a fresh object literal each render.
  const variablesKey = JSON.stringify(variables)
  // Snapshot the serialized variables so the effect (keyed on variablesKey)
  // never closes over a stale-by-reference `variables`.
  const variablesSnapshot = useMemo<Record<string, unknown>>(
    () => JSON.parse(variablesKey) as Record<string, unknown>,
    [variablesKey],
  )

  useEffect(() => {
    if (bodyTemplate === '') {
      setSubject(null)
      setBody('')
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    const timer = setTimeout(() => {
      api
        .post<PreviewResponse>(
          '/admin/conversations/notification-template-preview',
          { subjectTemplate, bodyTemplate, variables: variablesSnapshot },
        )
        .then((resp) => {
          if (cancelled) return
          setSubject(resp.subject ?? null)
          setBody(resp.body ?? '')
          setError(null)
        })
        .catch((e: Error) => {
          if (!cancelled) setError(e.message)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [subjectTemplate, bodyTemplate, variablesSnapshot])

  return { subject, body, loading, error }
}
